/* global simplified_hash, formatter_data */

import jQuery from "jquery";

import { isFormat, isFDA, isAPAP, isLegacyFDA, isAggregate, isCanadian } from "./format";
import { adjustNewLinearLineHeightForCalorieFont, showFootnoteSeparator } from "./layout";
import { tabularShuffle, tabularShuffleLeft } from "./layout";
import { rebuildFunction } from "./vitamin_toggles";
import { minifyAsteriskSection } from "./recipe_option_toggles";
import {
  SELECTOR_FOR_NUTRIENT_WITH_VISIBILITY_TOGGLE,
  insignificantCheckboxForFormat,
  formatSupportsVisibilityToggle,
} from "./optional_nutrient_toggles.js";

const $ = jQuery;

export const setSimplified = (value, { updateControls = false } = {}) => {
  /* Do not allow a true value if the current format disallows it */
  value &&= formatCanSimplify();

  $(".NFP").toggleClass("simplified", value);
  simplifiedCheckboxForFormat()?.prop("checked", value);
  simplify({ updateControls });
  syncInsignificantToggleVisibility(value);
  minifyAsteriskSection();

  /* rebuildFunction does not guard against these window properties not being available */
  if (isFDA() && isAPAP() && !("optional_vitamins_2" in window)) {
    return;
  } else if (isLegacyFDA() && isAPAP() && !("optional_vitamins_2" in window)) {
    return;
  } else {
    rebuildFunction();
  }
};

export const formatCanSimplify = () => formatter_data.format_can_simplify;

export const simplifiedCheckboxForFormat = () => {
  if (isLegacyFDA()) {
    return $("#simplifiedformattoggle");
  } else if (isFDA()) {
    return isAggregate()
      ? $("#newaggregatesimplifiedformattoggle")
      : $("#newsimplifiedformattoggle");
  } else if (isCanadian()) {
    return $("#newcanadasimplifiedformattoggle");
  }
};

function isNutrientVisible(format, nutrient, isSimplified) {
  const canToggleVisibility = SELECTOR_FOR_NUTRIENT_WITH_VISIBILITY_TOGGLE[format][nutrient];
  if (!canToggleVisibility) return isSimplified;

  const checked = $(`#${nutrient}-visibility-toggle`).is(":checked");
  return isSimplified ? checked : !checked;
}

function fadeInOrOutNutrients(format, isSimplified, callback) {
  const changed = [];
  Object.entries(SELECTOR_FOR_NUTRIENT_WITH_VISIBILITY_TOGGLE[format]).forEach(
    ([nutrient, selector]) => {
      if (!simplified_hash[nutrient]) return; // is significant

      const nutrientVisible = isNutrientVisible(format, nutrient, isSimplified);
      const animation = nutrientVisible ? "fadeIn" : "fadeOut";

      changed.push($(selector)[animation]("slow", callback));
    }
  );
  return changed;
}

export function simplify({ updateControls = true } = {}) {
  if (isFormat("legacy_fda/adult/vertical")) {
    if ($("#simplifiedformattoggle").is(":checked")) {
      //check for each non top 5 nutrient and hide it if it's 'true'
      if (simplified_hash["calories_fat"]) {
        $("#fatcalories").fadeOut("slow");
      }

      if (simplified_hash["fat_saturated"]) {
        $("#saturated_fat").fadeOut("slow");
      }

      if (simplified_hash["fat_trans"]) {
        $("#trans_fat").fadeOut("slow");
      }

      if (simplified_hash["cholesterol"]) {
        $("#cholesterol").fadeOut("slow");
      }

      if (simplified_hash["fiber"]) {
        $("#fiber").fadeOut("slow");
      }

      if (simplified_hash["sugar"]) {
        $("#sugar").fadeOut("slow");
      }

      //hide footnote section
      if (updateControls) {
        $("#asterisksectionminitoggle").prop("checked", true);
      }
    } else {
      if (simplified_hash["calories_fat"]) {
        $("#fatcalories").fadeIn("slow");
      }

      if (simplified_hash["fat_saturated"]) {
        $("#saturated_fat").fadeIn("slow");
      }

      if (simplified_hash["fat_trans"]) {
        $("#trans_fat").fadeIn("slow");
      }

      if (simplified_hash["cholesterol"]) {
        $("#cholesterol").fadeIn("slow");
      }

      if (simplified_hash["fiber"]) {
        $("#fiber").fadeIn("slow");
      }

      if (simplified_hash["sugar"]) {
        $("#sugar").fadeIn("slow");
      }

      if (updateControls) {
        $("#asterisksectionminitoggle").prop("checked", false);
        $("#insignificanttoggle").prop("checked", false);
      }
    }
  } else if (isFormat("fda/adult/vertical")) {
    const isSimplified = $("#newsimplifiedformattoggle").is(":checked");

    fadeInOrOutNutrients("fda/adult/vertical", isSimplified);

    const asteriskFn = isSimplified ? "hide" : "show";
    $(".asterisksection-new-vertical")[asteriskFn]();
    $(".dailyvalue-asterisk")[asteriskFn]();
    showFootnoteSeparator();
  } else if (
    isFormat("canada/adult/vertical") ||
    isFormat("canada/adult/tabular") ||
    isFormat("canada/adult/vertical/aggregate")
  ) {
    if ($("#newcanadasimplifiedformattoggle").is(":checked")) {
      //check for each non top 4 nutrients and hide it if it's 'true'
      if (
        simplified_hash["fat_saturated"] &&
        simplified_hash["fat_trans"] &&
        simplified_hash["fat_saturated_and_fat_trans"]
      ) {
        // need all to be 0 to hide them
        $(".new-canada-saturated-fat").fadeOut("slow");
        $(".new-canada-trans-fat").fadeOut("slow");
      }
      if (simplified_hash["fiber"]) {
        $("#new-canada-fiber").fadeOut("slow");
      }

      if (simplified_hash["sugar"]) {
        $("#new-canada-sugar").fadeOut("slow");
      }

      if (simplified_hash["cholesterol"]) {
        $("#new-canada-cholesterol").fadeOut("slow");
        $(".cholesteroldvtoggle").hide();
      }

      if (simplified_hash["sodium"]) {
        $("#new-canada-sodium").fadeOut("slow");
      }

      if (simplified_hash["potassium"]) {
        $("#new-canada-potassium").fadeOut("slow");
      }

      if (simplified_hash["calcium"]) {
        $("#new-canada-calcium").fadeOut("slow");
      }

      if (simplified_hash["iron"]) {
        $("#new-canada-iron").fadeOut("slow");
      }

      $("#new-canada-vertical-insignificant-nutrients-list").fadeIn("slow");
    } else {
      if (
        simplified_hash["fat_saturated"] &&
        simplified_hash["fat_trans"] &&
        simplified_hash["fat_saturated_and_fat_trans"]
      ) {
        // need all to be 0 to hide them
        $(".new-canada-saturated-fat").fadeIn("slow");
        $(".new-canada-trans-fat").fadeIn("slow");
      }
      if (simplified_hash["fiber"]) {
        $("#new-canada-fiber").fadeIn("slow");
      }

      if (simplified_hash["sugar"]) {
        $("#new-canada-sugar").fadeIn("slow");
      }

      if (simplified_hash["cholesterol"]) {
        $("#new-canada-cholesterol").fadeIn("slow");
        $(".cholesteroldvtoggle").show();
      }

      if (simplified_hash["sodium"]) {
        $("#new-canada-sodium").fadeIn("slow");
      }

      $("#new-canada-vertical-insignificant-nutrients-list").fadeOut("slow");
    }
  } else if (isFormat("canada/adult/linear")) {
    if ($("#newcanadasimplifiedformattoggle").is(":checked")) {
      //check for each non top 4 nutrients and hide it if it's 'true'
      if (
        simplified_hash["fat_saturated"] &&
        simplified_hash["fat_trans"] &&
        simplified_hash["fat_saturated_and_fat_trans"]
      ) {
        // need all to be 0 to hide them
        $(".satfatnewlinearcanada").fadeOut("slow");
        $(".transfatnewlinearcanada").fadeOut("slow");
      }
      if (simplified_hash["fiber"]) {
        $(".fibernewlinearcanada").fadeOut("slow");
      }

      if (simplified_hash["sugar"]) {
        $(".sugarnewlinearcanada").fadeOut("slow");
      }

      if (simplified_hash["cholesterol"]) {
        $(".cholesterolnewlinearcanada").fadeOut("slow");
        $(".cholesteroldvtoggle").hide();
      }

      if (simplified_hash["sodium"]) {
        $(".sodiumnewlinearcanada").hide();
      }

      $(".insignificant-nutrients-list-new-linear-canada").fadeIn("slow");
      $(".newcanadalinear-dailyvalue-simplified").show();
      $(".newcanadalinear-dailyvalue").hide();

      // remove extra comma if we need
      $(".endofvitaminsnewlinearcanada").each(function () {
        const last_vitamin = $(this).prev("div:visible").children().last();
        if (last_vitamin.length == 0) {
          const last_nutrient = $(this).parent().prevAll("div:visible").first().children().last();
          if (last_nutrient.length > 0) {
            last_nutrient.html(last_nutrient.html().split(",")[0]);
          }
        }
      });
    } else {
      // in case need to add comma back to last nutrient
      $(".endofvitaminsnewlinearcanada").each(function () {
        const last_nutrient = $(this).parent().prevAll("div:visible").first().children().last();
        if (last_nutrient.length > 0 && last_nutrient.html().split(",").length < 2) {
          last_nutrient.html(last_nutrient.html() + ",&#8197;");
        }
      });

      if (
        simplified_hash["fat_saturated"] &&
        simplified_hash["fat_trans"] &&
        simplified_hash["fat_saturated_and_fat_trans"]
      ) {
        // need all to be 0 to hide them
        $(".satfatnewlinearcanada").fadeIn("slow");
        $(".transfatnewlinearcanada").fadeIn("slow");
      }
      if (simplified_hash["fiber"]) {
        $(".fibernewlinearcanada").fadeIn("slow");
      }

      if (simplified_hash["sugar"]) {
        $(".sugarnewlinearcanada").fadeIn("slow");
      }

      if (simplified_hash["cholesterol"]) {
        $(".cholesterolnewlinearcanada").fadeIn("slow");
        $(".cholesteroldvtoggle").show();
      }

      if (simplified_hash["sodium"]) {
        $(".sodiumnewlinearcanada").fadeIn("slow");
      }

      $(".insignificant-nutrients-list-new-linear-canada").fadeOut("slow");
      $(".newcanadalinear-dailyvalue-simplified").hide();
      $(".newcanadalinear-dailyvalue").show();
    }
  } else if (isFormat("legacy_fda/adult/linear")) {
    if ($("#simplifiedformattoggle").is(":checked")) {
      if (simplified_hash["calories_fat"]) {
        $("#fatcalorieslinear").fadeOut("slow");
      }

      if (simplified_hash["fat_saturated"]) {
        $("#satfatlinear").fadeOut("slow");
      }

      if (simplified_hash["fat_trans"]) {
        $("#transfatlinear").fadeOut("slow");
      }

      if (simplified_hash["cholesterol"]) {
        $("#cholesterollinear").fadeOut("slow");
      }

      if (simplified_hash["fiber"]) {
        $("#fiberlinear").fadeOut("slow");
      }

      if (simplified_hash["sugar"]) {
        $("#sugarlinear").fadeOut("slow");
      }
    } else {
      if (simplified_hash["calories_fat"]) {
        $("#fatcalorieslinear").fadeIn("slow");
      }

      // satfat and trans different for canada - both have to be 0 to hide
      if (simplified_hash["fat_saturated"]) {
        $("#satfatlinear").fadeIn("slow");
      }

      if (simplified_hash["fat_trans"]) {
        $("#transfatlinear").fadeIn("slow");
      }

      if (simplified_hash["cholesterol"]) {
        $("#cholesterollinear").fadeIn("slow");
      }

      if (simplified_hash["fiber"]) {
        $("#fiberlinear").fadeIn("slow");
      }

      if (simplified_hash["sugar"]) {
        $("#sugarlinear").fadeIn("slow");
      }
    }
  } else if (isFormat("fda/adult/linear")) {
    const isSimplified = $("#newsimplifiedformattoggle").is(":checked");

    fadeInOrOutNutrients("fda/adult/linear", isSimplified, adjustNewLinearLineHeightForCalorieFont);
  } else if (isFormat("legacy_fda/adult/tabular")) {
    if ($("#simplifiedformattoggle").is(":checked")) {
      if (simplified_hash["calories_fat"]) {
        $("#tabularfatcalories").fadeOut("slow");
      }

      if (simplified_hash["fat_saturated"]) {
        $("#satfattabular").fadeOut("slow");
      }

      if (simplified_hash["fat_trans"]) {
        $("#transfattabular").fadeOut("slow");
      }

      if (simplified_hash["cholesterol"]) {
        $("#cholesteroltabular").fadeOut("slow");
      }

      if (simplified_hash["fiber"]) {
        $("#fibertabular").fadeOut("slow");
      }

      if (simplified_hash["sugar"]) {
        $("#sugartabular").fadeOut("slow");
      }

      if (updateControls) {
        $("#asterisksectionminitoggle").prop("checked", true);
      }

      tabularShuffleLeft();
    } else {
      $("#tabular_nutrient_cols").css("width", $("#tabularslider").slider("value") * 2 + 7);
      $(".tabular_nutrient_col:last").show();
      $("#filler_col_2").appendTo("#tabularnutritionfactstable .nutrients_col_2:visible");
      if (simplified_hash["calories_fat"]) {
        $("#tabularfatcalories").fadeIn("slow");
      }

      if (simplified_hash["fat_saturated"]) {
        $("#satfattabular").fadeIn("slow");
      }

      if (simplified_hash["fat_trans"]) {
        $("#transfattabular").fadeIn("slow");
      }

      if (simplified_hash["cholesterol"]) {
        $("#cholesteroltabular").fadeIn("slow");
      }

      if (simplified_hash["fiber"]) {
        $("#fibertabular").fadeIn("slow");
      }

      if (simplified_hash["sugar"]) {
        $("#sugartabular").fadeIn("slow");
      }

      tabularShuffle();

      if (updateControls) {
        $("#asterisksectionminitoggle").prop("checked", false);
      }
    }
  } else if (isFormat("fda/adult/tabular")) {
    const isSimplified = $("#newsimplifiedformattoggle").is(":checked");

    const nutrientsChanged = fadeInOrOutNutrients("fda/adult/tabular", isSimplified);

    let shuffleFn;
    if (isSimplified) {
      $("#new_tabular_asterisk_col").hide();
      $("#new_tabular_dual_asterisk_row").fadeOut("slow");
      $(".new_tabular_dual_nutrient_col").addClass("no-padding-no-border");
      $("#new_tabular_daily_value_explanation").fadeIn("slow");
      $(".dailyvalue-asterisk").hide();
      $(".longform").hide();
      $(".abbreviated").show();
      shuffleFn = tabularShuffleLeft;
    } else {
      $("new_tabular_dual_nutrient_col:last, .new_tabular_nutrient_col:last").show();
      $("#new_tabular_dual_nutrient_cols").children(".new_tabular_dual_nutrient_col:last").show();
      $(".new_tabular_dual_nutrient_col").removeClass("no-padding-no-border");
      $("#new_filler_col_2").show();
      $("#new_tabular_daily_value_explanation").fadeOut("slow");
      $("#new_tabular_asterisk_col").fadeIn("slow");
      $("#new_tabular_dual_asterisk_row").fadeIn("slow");
      $(".abbreviated").hide();
      $(".dailyvalue-asterisk").show();
      $(".longform").show();
      shuffleFn = tabularShuffle; // don't need vitamin shuffle bc rebuild() called after already and that will trigger that
    }

    Promise.all(nutrientsChanged.map((el) => el.promise())).then(shuffleFn);
  } else if (isFormat("legacy_fda/adult/vertical/as_prepared")) {
    if ($("#simplifiedformattoggle").is(":checked")) {
      if (simplified_hash["calories_fat"]) {
        $("#fatcaloriesaspackaged").fadeOut("slow");
      }

      if (simplified_hash["fat_saturated"]) {
        $("#satfataspackaged").fadeOut("slow");
      }

      if (simplified_hash["fat_trans"]) {
        $("#transfataspackaged").fadeOut("slow");
      }

      if (simplified_hash["cholesterol"]) {
        $("#cholesterolaspackaged").fadeOut("slow");
      }

      if (simplified_hash["fiber"]) {
        $("#fiberaspackaged").fadeOut("slow");
      }

      if (simplified_hash["sugar"]) {
        $("#sugaraspackaged").fadeOut("slow");
      }

      //hide footnote section
      if (updateControls) {
        $("#asterisksectionminitoggle").prop("checked", true);
      }
    } else {
      if (simplified_hash["calories_fat"]) {
        $("#fatcaloriesaspackaged").fadeIn("slow");
      }

      if (simplified_hash["fat_saturated"]) {
        $("#satfataspackaged").fadeIn("slow");
      }

      if (simplified_hash["fat_trans"]) {
        $("#transfataspackaged").fadeIn("slow");
      }

      if (simplified_hash["cholesterol"]) {
        $("#cholesterolaspackaged").fadeIn("slow");
      }

      if (simplified_hash["fiber"]) {
        $("#fiberaspackaged").fadeIn("slow");
      }

      if (simplified_hash["sugar"]) {
        $("#sugaraspackaged").fadeIn("slow");
      }

      if (updateControls) {
        $("#asterisksectionminitoggle").prop("checked", false);
        $("#insignificanttoggle").prop("checked", false);
      }
    }
  } else if (isFormat("fda/adult/vertical/as_prepared")) {
    const isSimplified = $("#newsimplifiedformattoggle").is(":checked");

    fadeInOrOutNutrients("fda/adult/vertical/as_prepared", isSimplified);

    const asteriskFn = isSimplified ? "hide" : "show";
    $(".asterisksection-new-vertical")[asteriskFn]();
    $(".dailyvalue-asterisk")[asteriskFn]();
    showFootnoteSeparator();
  } else if (isFormat("fda/adult/vertical/aggregate")) {
    const isSimplified = $("#newaggregatesimplifiedformattoggle").is(":checked");

    fadeInOrOutNutrients("fda/adult/vertical/aggregate", isSimplified);

    $(".asterisksection-new-vertical")[isSimplified ? "fadeOut" : "fadeIn"]();
    $(".dailyvalue-asterisk")[isSimplified ? "hide" : "show"]();
    if (!isSimplified && updateControls) {
      $("#newaggregateinsignificanttoggle").prop("checked", false);
    }
  }
}

// Checkbox for insignificant nutrients visibility is only relevant in simplified mode
function syncInsignificantToggleVisibility(simplified) {
  if (!formatSupportsVisibilityToggle()) return;

  const insignificant = insignificantCheckboxForFormat();
  if (!insignificant || insignificant.length === 0) return;

  insignificant.closest(".panel-block").toggle(simplified);
}
